/**
 * Reconciliations service — Bind Operations (AG-Extracto).
 * Desde 2026-08-17 los endpoints /bind-ops/* viven en el stack bind-scrapper
 * (repo Cucuru-com/bind-scrapper), con API propia: VITE_API_BASE_BIND. Misma
 * auth (el JWT del panel vale allá). Fallback a VITE_API_BASE mientras dure el
 * cutover / para dev local.
 */
const API_BASE =
  (import.meta.env.VITE_API_BASE_BIND as string | undefined) ??
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  "http://localhost:3000";

export type RunStatus = "all_found" | "partial" | "error" | "loaded";

export type TxnStatus = "pendiente" | "conciliada" | "perdida" | "redisparada";

export interface Transaction {
  referencia:         string;
  account:            "45" | "84";
  importe:            number | null;
  contraparte_cuit:   string | null;
  contraparte_nombre: string | null;
  fecha_valor:        string | null;   // YYYY-MM-DD
  descripcion:        string | null;
  status:             TxnStatus;
  first_seen_at?:     string | null;
  last_run_id?:       string | null;
}

export interface ListTxnsParams {
  account?: "45" | "84";
  status?:  TxnStatus;
  from?:    string;   // YYYY-MM-DD (filtra por fecha_valor)
  to?:      string;   // YYYY-MM-DD
  limit?:   number;
  cursor?:  string;
}

// "Otros movimientos": todo lo que NO es Transferencia Credito (débitos, impuestos…).
export interface Movimiento {
  mov_key:            string;
  referencia:         string | null;
  account:            "45" | "84";
  importe:            number | null;
  saldo:              number | null;
  categoria:          string | null;
  contraparte_cuit:   string | null;
  contraparte_nombre: string | null;
  fecha_valor:        string | null;
  descripcion:        string | null;
  // CP-421: vínculo reversa ↔ transferencia original (lo escribe el reconciler de bind-scrapper).
  reversa_estado?:       "apareada" | "ambigua" | "sin_aparear" | null;  // en la fila de la reversa
  reversa_de?:           string | null;      // mov_key del original apareado
  reversa_candidatos?:   string[];           // mov_keys candidatos cuando es ambigua
  reversa_detectada_at?: string | null;
  reversada_por?:        string | null;      // en el original: mov_key de la reversa
  reversada_at?:         string | null;
}

export interface ListMovsParams {
  account: "45" | "84";
  from?:   string;
  to?:     string;
  limit?:  number;
  cursor?: string;
  /** CP-421: sólo reversas y sus transferencias originales. */
  reversas?: boolean;
}

export interface ConciliarResult {
  account?:       string;
  received?:      number;
  conciliadas:    number;
  redisparadas?:  number;
  perdidas:       number;
  pendientes_redisparo?: number;
  triggered_by?:  string;
}

// Cron del scraper (cutover AWS ⇄ Windows box).
// Ciclo con valores fijos (también validados en el backend) — nada de campo abierto.
export const CRON_RATE_OPTIONS = [30, 20, 15, 10, 5];

export interface CronState {
  configured:    boolean;
  name?:         string;
  state?:        "ENABLED" | "DISABLED";
  schedule?:     string;
  rate_minutes?: number | null;    // rate VIVO de la regla (en recuperación es el escalón, no el ciclo)
  cycle_minutes?: number | null;   // ciclo configurado por el usuario (el del selector)
  backoff_minutes?: number | null; // != null → modo recuperación (reconciler acelerado por pendientes)
  backoff_since?: string | null;
  next_run_at?:  string | null;  // solo en la respuesta de un cambio (rate/enable)
}

export async function getCronState(): Promise<CronState> {
  const r = await authFetch(`${API_BASE}/bind-ops/cron`, { headers: authHeader() });
  if (!r.ok) throw new Error(`getCronState ${r.status}: ${await r.text()}`);
  return r.json();
}

export async function setCronState(action: "enable" | "disable"): Promise<CronState> {
  const r = await authFetch(`${API_BASE}/bind-ops/cron`, {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
    body: JSON.stringify({ action }),
  });
  if (!r.ok) throw new Error(`setCronState ${r.status}: ${await r.text()}`);
  return r.json();
}

// Cambia el ciclo del cron (minutos, uno de CRON_RATE_OPTIONS).
// Cambiarlo resetea la programación (next_run_at = ahora + minutos) y, si el
// reconciler estaba en modo recuperación, lo saca (el cambio manual manda).
export async function setCronRate(minutes: number): Promise<CronState> {
  const r = await authFetch(`${API_BASE}/bind-ops/cron`, {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
    body: JSON.stringify({ action: "rate", minutes }),
  });
  if (!r.ok) throw new Error(`setCronRate ${r.status}: ${await r.text()}`);
  return r.json();
}

// ----- Credenciales BIND del scraper (la clave expira cada ~75-90 días) ------
export type CredAlertCause =
  | "password_expired" | "user_locked" | "single_session"
  | "bad_credentials" | "network" | "unknown";

export interface ScraperCredsState {
  username:               string | null;
  secret_last_changed_at: string | null;   // LastChangedDate del secret (ISO)
  last_changed_at:        string | null;   // último cambio hecho desde webadmin
  last_changed_by:        string | null;
  alert: {
    cause:             CredAlertCause;
    first_detected_at: string | null;
    last_alerted_at:   string | null;
    alert_count:       number;
  } | null;                                // != null → clave expirada/bloqueada detectada
}

export interface ScraperCredsUpdateResult {
  ok:           boolean;
  username:     string | null;
  changed_by:   string;
  changed_at:   string;
  validated_at: string | null;
}

export async function getScraperCreds(): Promise<ScraperCredsState> {
  const r = await authFetch(`${API_BASE}/bind-ops/credentials`, { headers: authHeader() });
  if (!r.ok) throw new Error(`getScraperCreds ${r.status}: ${await r.text()}`);
  return r.json();
}

// Valida con UN login CAS real contra id.bind.com.ar antes de guardar; si BIND
// rechaza (o hay una sesión activa — el scraper corriendo), el secret no se toca.
export async function updateScraperCreds(password: string): Promise<ScraperCredsUpdateResult> {
  const r = await authFetch(`${API_BASE}/bind-ops/credentials`, {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
  if (!r.ok) {
    const body = await r.json().catch(() => ({} as any));
    throw new Error(body.error ?? `updateScraperCreds ${r.status}`);
  }
  return r.json();
}

// Corrida MANUAL inmediata del scraper (async). NO toca la programación del cron.
export async function runScraperNow(): Promise<{ invoked: boolean; by?: string }> {
  const r = await authFetch(`${API_BASE}/bind-ops/cron/run`, {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
  });
  if (!r.ok) throw new Error(`runScraperNow ${r.status}: ${await r.text()}`);
  return r.json();
}

// Uptime de webhooks: % de transferencias recibidas vs las que no llegaron.
export interface UptimeStat {
  account?:      string;
  total:         number;
  fallidas:      number;   // webhook no llegó (was_missing)
  recibidas:     number;
  uptime_pct:    number | null;
  fallidas_pct:  number | null;
}
export interface UptimeWindow extends UptimeStat {
  from:       string;        // inicio de la ventana (ISO)
  by_account: UptimeStat[];
}
export interface Uptime extends UptimeStat {
  by_account: UptimeStat[];
  desde?:     string | null; // inicio del histórico (primera corrida registrada)
  windows?:   { hora: UptimeWindow; dia: UptimeWindow; mes: UptimeWindow };
}

export async function getUptime(): Promise<Uptime> {
  const r = await authFetch(`${API_BASE}/bind-ops/uptime`, { headers: authHeader() });
  if (!r.ok) throw new Error(`getUptime ${r.status}: ${await r.text()}`);
  return r.json();
}

export type AlertLevel = "error" | "warning" | "info";

export interface BindAlert {
  ts:       string;        // ISO
  level:    AlertLevel;
  source:   string;        // scraper | reconciler | conciliar
  account?: string | null;
  message:  string;
  run_id?:  string | null;
  emailed?: boolean;
}

export interface ListAlertsParams {
  level?:  AlertLevel;
  limit?:  number;
  cursor?: string;
}

export interface ReconciliationRunSummary {
  run_id:              string;
  account:             "45" | "84";
  product:             string;
  started_at:          string;   // ISO
  finished_at:         string;
  total_ids_extracted: number;
  missing_count:       number;
  status:              RunStatus;
  error_message?:      string | null;
}

export interface ReconciliationRunDetail extends ReconciliationRunSummary {
  missing_ids:      string[];
  refired_ids:      string[];
  api_response_raw?: string | null;
  xls_s3_key?:     string | null;
  xls_url?:        string | null;   // presigned ~15 min
}

export interface ReconciliationDetail {
  run_id: string;
  items:  ReconciliationRunDetail[];
}

export interface ListRunsParams {
  limit?:   number;
  cursor?:  string;
  account?: "45" | "84";
  date?:    string;   // YYYY-MM-DD
}

function authHeader(): Record<string, string> {
  const jwt = localStorage.getItem("webadmin2.jwt");
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

async function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(url, init);
  if (res.status === 401) {
    localStorage.removeItem("webadmin2.jwt");
    localStorage.removeItem("webadmin2.email");
    if (typeof window !== "undefined") window.location.replace("/login");
  }
  return res;
}

export async function listReconciliationRuns(p: ListRunsParams = {}): Promise<{
  items:  ReconciliationRunSummary[];
  cursor: string | null;
}> {
  const qs = new URLSearchParams();
  if (p.limit)   qs.set("limit",   String(p.limit));
  if (p.cursor)  qs.set("cursor",  p.cursor);
  if (p.account) qs.set("account", p.account);
  if (p.date)    qs.set("date",    p.date);
  const url = `${API_BASE}/bind-ops/runs${qs.toString() ? `?${qs}` : ""}`;
  const r = await authFetch(url, { headers: authHeader() });
  if (!r.ok) throw new Error(`listReconciliationRuns ${r.status}: ${await r.text()}`);
  return r.json();
}

export async function getReconciliationDetail(runId: string): Promise<ReconciliationDetail> {
  const r = await authFetch(`${API_BASE}/bind-ops/runs/${encodeURIComponent(runId)}`, {
    headers: authHeader(),
  });
  if (!r.ok) throw new Error(`getReconciliationDetail ${r.status}: ${await r.text()}`);
  return r.json();
}

export async function listTransactions(p: ListTxnsParams = {}): Promise<{
  items:  Transaction[];
  cursor: string | null;
}> {
  const qs = new URLSearchParams();
  if (p.account) qs.set("account", p.account);
  if (p.status)  qs.set("status",  p.status);
  if (p.from)    qs.set("from",    p.from);
  if (p.to)      qs.set("to",      p.to);
  if (p.limit)   qs.set("limit",   String(p.limit));
  if (p.cursor)  qs.set("cursor",  p.cursor);
  const url = `${API_BASE}/bind-ops/transactions${qs.toString() ? `?${qs}` : ""}`;
  const r = await authFetch(url, { headers: authHeader() });
  if (!r.ok) throw new Error(`listTransactions ${r.status}: ${await r.text()}`);
  return r.json();
}

export async function listMovimientos(p: ListMovsParams): Promise<{
  items:  Movimiento[];
  cursor: string | null;
}> {
  const qs = new URLSearchParams();
  qs.set("account", p.account);
  if (p.from)   qs.set("from",   p.from);
  if (p.to)     qs.set("to",     p.to);
  if (p.limit)  qs.set("limit",  String(p.limit));
  if (p.cursor) qs.set("cursor", p.cursor);
  if (p.reversas) qs.set("reversas", "1");
  const url = `${API_BASE}/bind-ops/movimientos?${qs}`;
  const r = await authFetch(url, { headers: authHeader() });
  if (!r.ok) throw new Error(`listMovimientos ${r.status}: ${await r.text()}`);
  return r.json();
}

/** Re-conciliación manual ("Re-conciliar ahora"): fuerza el flujo automático ya mismo
 *  para una cuenta (diff contra received_payments + re-disparo de las perdidas reales).
 *  La conciliación normal corre sola en cada ciclo del reconciler. */
export async function conciliar(p: { account: "45" | "84" }): Promise<ConciliarResult> {
  const r = await authFetch(`${API_BASE}/bind-ops/conciliar`, {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
    body: JSON.stringify(p),
  });
  if (!r.ok) throw new Error(`conciliar ${r.status}: ${await r.text()}`);
  return r.json();
}

/** Log de alertas del pipeline (errores de scraping/login/conciliación), equivalente
 *  a lo que se envía por email a operaciones. Lo consume la pantalla AG-Extracto. */
export async function listAlerts(p: ListAlertsParams = {}): Promise<{
  items:  BindAlert[];
  cursor: string | null;
}> {
  const qs = new URLSearchParams();
  if (p.level)  qs.set("level",  p.level);
  if (p.limit)  qs.set("limit",  String(p.limit));
  if (p.cursor) qs.set("cursor", p.cursor);
  const url = `${API_BASE}/bind-ops/alerts${qs.toString() ? `?${qs}` : ""}`;
  const r = await authFetch(url, { headers: authHeader() });
  if (!r.ok) throw new Error(`listAlerts ${r.status}: ${await r.text()}`);
  return r.json();
}
